import { useEffect, useState } from 'react';

/** Pulls the API's error message out of an axios failure. */
export function apiMessage(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: string | string[] } } }).response?.data
    ?.message;
  if (Array.isArray(message)) return message.join(', ');
  return message ?? fallback;
}

/**
 * The HTTP status behind a failed call, when there is one.
 *
 * Some refusals are worth handling rather than reporting: a 422 from
 * `POST /sessions/:id/close` means the visit still has work on it, which the
 * operator can finish — a toast quoting `force=true` is not something they can
 * act on from where they are standing.
 */
export function statusOf(error: unknown): number | undefined {
  return (error as { response?: { status?: number } }).response?.status;
}

/** Drops empty values — the global ValidationPipe rejects a blank query param. */
export function pruneParams(params: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== '' && value !== undefined && value !== null),
  );
}

export function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}
